import { useEffect, useState } from 'react';
import { outOfBudgetBoostApi } from '@/api/client';
import { OutOfBudgetBoostModal } from '@/components/workspace/OutOfBudgetBoostModal';
import {
  apiErrorMessage,
  parseOutOfBudgetBoostSettings,
  type OutOfBudgetBoostSavePayload,
  type OutOfBudgetBoostSettings,
} from '@/utils/outOfBudgetBoost';

type CampaignTarget = {
  scope: 'campaign';
  campaignId: string;
  name: string;
  adProduct: string;
};

type PortfolioTarget = {
  scope: 'portfolio';
  portfolioId: string;
  name: string;
};

export function WorkspaceOutOfBudgetBoostModal({
  profileId,
  target,
  onClose,
  onChanged,
}: {
  profileId: string;
  target: CampaignTarget | PortfolioTarget;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [initial, setInitial] = useState<OutOfBudgetBoostSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const targetKey = target.scope === 'campaign' ? target.campaignId : target.portfolioId;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const request =
      target.scope === 'campaign'
        ? outOfBudgetBoostApi.getCampaign(profileId, target.campaignId, target.adProduct)
        : outOfBudgetBoostApi.getPortfolio(profileId, target.portfolioId);
    void request
      .then((res) => {
        if (cancelled) return;
        if (!res.success) {
          setError(res.message || 'Failed to load out-of-budget settings');
          return;
        }
        setInitial(parseOutOfBudgetBoostSettings(res.data));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(apiErrorMessage(err, 'Failed to load out-of-budget settings'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [profileId, target, targetKey]);

  async function handleSave(payload: OutOfBudgetBoostSavePayload) {
    setSaving(true);
    setError(null);
    try {
      const res =
        target.scope === 'campaign'
          ? await outOfBudgetBoostApi.saveCampaign(
              profileId,
              target.campaignId,
              target.adProduct,
              payload
            )
          : await outOfBudgetBoostApi.savePortfolio(profileId, target.portfolioId, payload);
      if (!res.success) {
        setError(res.message || 'Failed to save out-of-budget settings');
        return;
      }
      onChanged();
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to save out-of-budget settings'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <OutOfBudgetBoostModal
      entityName={target.name}
      scopeLabel={target.scope}
      initial={initial}
      loading={loading}
      saving={saving}
      error={error}
      onClose={onClose}
      onSave={handleSave}
    />
  );
}
